import { useQuery } from "@tanstack/react-query";
import { getLlmUsageDashboard } from "../services/llmUsageApi";
import {
  LlmUsageQueryParams,
  LlmUsageDashboardData,
  BreakdownItem,
  TopItem,
  BackendBreakdownItem,
  BackendTopSource,
  BackendTopProject,
  BackendTopOperator,
  BackendTopArticle,
} from "../types/llm-usage.types";

const LLM_USAGE_QUERY_KEY = "llmUsageDashboard";

interface UseLlmUsageDashboardOptions {
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
}

const transformBreakdownItems = (
  items: BackendBreakdownItem[],
  labelKey: "boundedContext" | "operation" | "model"
): BreakdownItem[] => {
  return items.map((item) => ({
    label: item[labelKey] || "unknown",
    totalCostUsd: item.totalCostUsd,
    count: item.count,
  }));
};

const transformTopSources = (items: BackendTopSource[]): TopItem[] => {
  return items.map((item) => ({
    id: item.sourceId,
    name: item.sourceName,
    totalCostUsd: item.totalCostUsd,
    count: item.count,
    url: `/grants/sources?highlight=${item.sourceId}`,
  }));
};

const transformTopProjects = (items: BackendTopProject[]): TopItem[] => {
  return items.map((item) => ({
    id: item.projectId,
    name: item.projectName,
    totalCostUsd: item.totalCostUsd,
    count: item.count,
    url: `/grants/projects/${item.projectId}`,
  }));
};

const transformTopOperators = (items: BackendTopOperator[]): TopItem[] => {
  return items.map((item) => ({
    id: item.operatorId,
    name: item.operatorName,
    totalCostUsd: item.totalCostUsd,
    count: item.count,
    url: `/grants/operators/${item.operatorId}`,
  }));
};

const transformTopArticles = (items: BackendTopArticle[]): TopItem[] => {
  return items.map((item) => ({
    id: item.articleId,
    name: item.articleTitle,
    totalCostUsd: item.totalCostUsd,
    count: item.count,
    url: item.articleUrl,
  }));
};

export const useLlmUsageDashboard = (options: UseLlmUsageDashboardOptions = {}) => {
  const { startDate, endDate, enabled = true } = options;

  const queryParams: LlmUsageQueryParams = {};
  if (startDate) queryParams.startDate = startDate;
  if (endDate) queryParams.endDate = endDate;

  return useQuery<LlmUsageDashboardData, Error>({
    queryKey: [LLM_USAGE_QUERY_KEY, startDate, endDate],
    queryFn: async () => {
      const response = await getLlmUsageDashboard(queryParams);
      const data = response.data;

      // Transform breakdown items to use consistent `label` property
      // Transform top items to use consistent `id/name/url` properties
      return {
        ...data,
        byBoundedContext: transformBreakdownItems(data.byBoundedContext, "boundedContext"),
        byOperation: transformBreakdownItems(data.byOperation, "operation"),
        byModel: transformBreakdownItems(data.byModel, "model"),
        topSources: transformTopSources(data.topSources),
        topProjects: transformTopProjects(data.topProjects),
        topOperators: transformTopOperators(data.topOperators),
        topArticles: transformTopArticles(data.topArticles),
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
};

export default useLlmUsageDashboard;
